import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Music, Folder, FileAudio, MessageSquare, Disc } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { search as searchApi } from '@/api/search';
import type {
  SearchResponse,
  SearchResult,
  SearchResultGroup,
  SearchResultType,
} from '@/types';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_ICONS: Record<SearchResultType, React.ReactNode> = {
  project: <Folder className="w-4 h-4" />,
  vibe: <Disc className="w-4 h-4" />,
  cut: <Music className="w-4 h-4" />,
  file: <FileAudio className="w-4 h-4" />,
  comment: <MessageSquare className="w-4 h-4" />,
};

const TYPE_COLORS: Record<SearchResultType, string> = {
  project: 'text-blue-400',
  vibe: 'text-purple-400',
  cut: 'text-green-400',
  file: 'text-orange-400',
  comment: 'text-yellow-400',
};

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query || query.length < 2) return text;
  
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);
  
  if (index === -1) return text;
  
  return (
    <>
      {text.slice(0, index)}
      <mark className="bg-primary/30 text-inherit rounded px-0.5">
        {text.slice(index, index + query.length)}
      </mark>
      {text.slice(index + query.length)}
    </>
  );
}

interface SearchResultItemProps {
  result: SearchResult;
  query: string;
  isSelected: boolean;
  onClick: () => void;
}

function SearchResultItem({ result, query, isSelected, onClick }: SearchResultItemProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-start gap-3 px-3 py-2 rounded-lg text-left transition-colors',
        isSelected ? 'bg-primary/20' : 'hover:bg-surface-light'
      )}
    >
      <div className={cn('mt-0.5 flex-shrink-0', TYPE_COLORS[result.type])}>
        {TYPE_ICONS[result.type]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-text truncate">
          {highlightMatch(result.title, query)}
        </div>
        <div className="text-xs text-muted truncate">
          {result.breadcrumb}
        </div>
        {result.subtitle && (
          <div className="text-xs text-muted/70 truncate mt-0.5">
            {highlightMatch(result.subtitle, query)}
          </div>
        )}
      </div>
      {result.matchedField !== 'name' && result.matchedField !== 'title' && (
        <span className="text-xs text-muted bg-surface-light px-1.5 py-0.5 rounded flex-shrink-0">
          in {result.matchedField}
        </span>
      )}
    </button>
  );
}

interface SearchResultGroupProps {
  group: SearchResultGroup;
  query: string;
  selectedIndex: number;
  startIndex: number;
  onSelect: (result: SearchResult) => void;
}

function SearchResultGroupComponent({
  group,
  query,
  selectedIndex,
  startIndex,
  onSelect,
}: SearchResultGroupProps) {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between px-3 py-1">
        <span className="text-xs font-semibold text-muted uppercase tracking-wide">
          {group.label}
        </span>
        {group.totalCount > group.results.length && (
          <span className="text-xs text-muted">
            {group.results.length} of {group.totalCount}
          </span>
        )}
      </div>
      <div className="space-y-0.5">
        {group.results.map((result, index) => (
          <SearchResultItem
            key={result.id}
            result={result}
            query={query}
            isSelected={selectedIndex === startIndex + index}
            onClick={() => onSelect(result)}
          />
        ))}
      </div>
    </div>
  );
}

export function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Flatten results for keyboard navigation
  const flatResults = useMemo(() => {
    if (!results) return [];
    return results.groups.flatMap((group) => group.results);
  }, [results]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults(null);
      setSelectedIndex(0);
      // Focus input after a short delay to ensure modal is rendered
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query || query.length < 2) {
      setResults(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const searchResults = await searchApi(query);
        setResults(searchResults);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search failed:', error);
        setResults(null);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // Navigate to selected result
  const handleSelect = useCallback(
    (result: SearchResult) => {
      onClose();
      navigate(result.resourceLink);
    },
    [navigate, onClose]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < flatResults.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (flatResults[selectedIndex]) {
            handleSelect(flatResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [flatResults, selectedIndex, handleSelect, onClose]
  );

  // Calculate start index for each group (for selection highlighting)
  const getGroupStartIndex = (groupIndex: number): number => {
    if (!results) return 0;
    let startIndex = 0;
    for (let i = 0; i < groupIndex; i++) {
      startIndex += results.groups[i].results.length;
    }
    return startIndex;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-card border-border max-w-3xl p-0 gap-0 overflow-hidden top-[20%] sm:top-[50%]">
        <DialogTitle className="sr-only">Search</DialogTitle>
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="w-5 h-5 text-muted flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search projects, vibes, cuts, files, comments..."
            className="flex-1 bg-transparent text-text placeholder-muted outline-none text-base"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 text-muted hover:text-text transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-1 text-xs text-muted">
            <kbd className="px-1.5 py-0.5 bg-surface-light rounded text-xs">esc</kbd>
            <span>to close</span>
          </div>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!isLoading && !results && query.length >= 2 && (
            <div className="text-center py-8 text-muted">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Searching...</p>
            </div>
          )}

          {!isLoading && results && results.totalResults === 0 && (
            <div className="text-center py-8 text-muted">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No results found for "{query}"</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          )}

          {!isLoading && results && results.totalResults > 0 && (
            <>
              {results.groups.map((group, groupIndex) => (
                <SearchResultGroupComponent
                  key={group.type}
                  group={group}
                  query={query}
                  selectedIndex={selectedIndex}
                  startIndex={getGroupStartIndex(groupIndex)}
                  onSelect={handleSelect}
                />
              ))}
              <div className="px-3 py-2 text-xs text-muted border-t border-border mt-2">
                {results.totalResults} result{results.totalResults !== 1 ? 's' : ''} found
              </div>
            </>
          )}

          {!isLoading && !query && (
            <div className="text-center py-8 text-muted">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Start typing to search</p>
              <p className="text-sm mt-1">
                Search across projects, vibes, cuts, files, and comments
              </p>
              <div className="flex items-center justify-center gap-4 mt-4 text-xs">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-surface-light rounded">↑</kbd>
                  <kbd className="px-1.5 py-0.5 bg-surface-light rounded">↓</kbd>
                  <span>navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-surface-light rounded">enter</kbd>
                  <span>select</span>
                </span>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
